import React from 'react';
import { motion } from 'framer-motion';
import { 
  Leaf, 
  Recycle, 
  Zap, 
  Globe, 
  ShieldCheck, 
  Droplets, 
  Wind, 
  Factory 
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

/**
 * Sürdürülebilirlik Sayfası
 * Çevresel inisiyatifler, yeşil teknoloji ve kurumsal sosyal sorumluluk odaklı.
 */
export default function Sustainability() {
  const sustainabilityPillars = [
    {
      icon: <Recycle className="w-10 h-10 text-primary" />,
      title: "Döngüsel Ekonomi",
      description: "Alüminyum %100 geri dönüştürülebilir bir metaldir. Üretim süreçlerimizde hurda geri kazanımını %95 seviyesine çıkararak doğal kaynakları koruyoruz.",
    },
    {
      icon: <Zap className="w-10 h-10 text-primary" />,
      title: "Enerji Verimliliği",
      description: "Ekstrüzyon hatlarımızda ve fırınlarımızda kullandığımız akıllı otomasyon sistemleri ile enerji tüketimini 2026 hedeflerimiz doğrultusunda %30 azalttık.",
    },
    {
      icon: <Wind className="w-10 h-10 text-primary" />,
      title: "Karbon Ayak İzi",
      description: "Yeşil enerji sertifikalı tedarik zincirimiz ve fabrika çatılarımızdaki güneş panelleri ile karbon emisyonlarımızı minimize ediyoruz.",
    },
    {
      icon: <Droplets className="w-10 h-10 text-primary" />,
      title: "Su Yönetimi",
      description: "Yüzey işlem ve dökümhane tesislerimizde kapalı devre su arıtma sistemleri kullanarak su israfını sıfıra indirmeyi hedefliyoruz.",
    },
  ];

  const initiatives = [
    {
      title: "Geri Dönüşüm ve Dökümhane",
      content: "Kendi dökümhanemizde işlediğimiz alüminyum biyetlerin büyük bir kısmı, fabrikamızdan çıkan ve piyasadan toplanan yüksek kaliteli hurdaların yeniden işlenmesiyle elde edilir. Bu süreç, birincil alüminyum üretimine göre %95 daha az enerji gerektirir.",
      image: IMAGES.SUSTAINABILITY_3,
    },
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-primary">
        <img 
          src={IMAGES.SUSTAINABILITY_1} 
          alt="Sürdürülebilirlik" 
          className="absolute inset-0 object-cover w-full h-full opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-transparent to-background" />
        
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Gelecek İçin <span className="text-accent">Sürdürülebilir</span> Çözümler
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
            className="text-xl text-white/90 max-w-3xl mx-auto"
          >
            Doğaya saygılı üretim teknolojileri ve döngüsel ekonomi modelimizle, alüminyumun gücünü yarınlar için dönüştürüyoruz.
          </motion.p>
        </div>
      </section>

      {/* Core Pillars Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sustainabilityPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-card border border-border rounded-xl shadow-sm hover:shadow-md transition-all group"
              >
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {pillar.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{pillar.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Initiatives */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col space-y-24">
            {initiatives.map((item, index) => (
              <div 
                key={index} 
                className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                <motion.div 
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex-1"
                >
                  <h2 className="text-3xl font-bold mb-6">{item.title}</h2>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {item.content}
                  </p>
                  <div className="flex items-center gap-4 text-primary font-semibold">
                    <ShieldCheck className="w-6 h-6" />
                    <span>ISO 14001 ve LEED Standartlarına Tam Uyum</span>
                  </div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="flex-1 w-full"
                >
                  <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR & Community Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <Globe className="w-64 h-64" />
            </div>
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl font-bold mb-6">Kurumsal Sosyal Sorumluluk</h2>
              <p className="text-lg text-white/80 mb-8">
                Sadece metal üretmiyoruz; toplumumuzun ve çalışanlarımızın geleceğine yatırım yapıyoruz. Yerel eğitim projeleri, mesleki gelişim bursları ve ağaçlandırma faaliyetlerimizle ekosistemimize değer katıyoruz.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-accent">50.000+</span>
                  <span className="text-sm text-white/60">Dikilen Fidan</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-accent">100+</span>
                  <span className="text-sm text-white/60">Eğitim Bursu</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-3xl font-bold text-accent">%100</span>
                  <span className="text-sm text-white/60">İş Güvenliği Odaklılık</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-accent/5 border-y border-border">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <Leaf className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Yeşil Üretim Sertifikalarımızı İnceleyin</h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Sürdürülebilirlik raporlarımız ve çevresel etki beyanlarımızı (EPD) indirmek için döküman merkezimizi ziyaret edebilirsiniz.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={ROUTE_PATHS.DOWNLOAD_CENTER} 
                className="px-8 py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-colors"
              >
                Döküman Merkezi
              </a>
              <a 
                href={ROUTE_PATHS.CONTACT} 
                className="px-8 py-3 bg-white border border-primary text-primary font-bold rounded-lg hover:bg-secondary transition-colors"
              >
                Detaylı Bilgi Al
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
